import type { ReportResult, ReportResultType } from '@/types/customReports.types';

/**
 * Helpers shared by every report-results consumer (MyReportsPage,
 * OnDemandHistoryPage, ReportResultViewer). Extracted so the per-row
 * logic stays consistent across the three legacy display3.php parities.
 */

/**
 * Returns the result-row primary key regardless of whether the row is a
 * CDM (`reportsOndemandResultsID`) or CST (`reportsCustomResultsID`).
 */
export function resultIdOf(row: ReportResult): number {
  const id = row.reportsOndemandResultsID ?? row.reportsCustomResultsID ?? 0;
  return Number(id);
}

/**
 * Returns the *order* id for a result row — the FK back to
 * `reports_cdm_orders` (CDM) or `reports_cst_orders` (CST). Used for the
 * legacy `report_visual_display3.php` "Generated" run-history dropdown and
 * for grouping rows on MyReportsPage.
 */
export function orderIdOf(row: ReportResult): number {
  const id = row.reportsCustomOrdersID ?? row.reportsOndemandOrdersID ?? 0;
  return Number(id);
}

/**
 * Maps a result's legacy `outputType` enum to a human label. Mirrors the
 * `<select id="outputType">` in `report_visual_display3.php`.
 */
export function outputTypeLabel(raw: string | null | undefined): string {
  switch (raw) {
    case 'cha':
      return 'Chart';
    case 'xls':
      return 'XLSX';
    case 'eml':
      return 'Email';
    case 'das':
      return 'Dashboard';
    case 'pbi':
      return 'Power BI';
    default:
      return raw || '—';
  }
}

/**
 * "cdm" or "cst" inferred from which order FK is populated on a row.
 * Defaults to "cst" since the v5 builder now writes every wizard order to
 * `reports_cst_orders` (see queue-builder-orders-via-cron change).
 */
export function resultTypeOf(row: ReportResult): ReportResultType {
  if (row.reportsCustomResultsID || row.reportsCustomOrdersID) return 'cst';
  if (row.reportsOndemandResultsID || row.reportsOndemandOrdersID) return 'cdm';
  return 'cst';
}

// ─── filterText parser ──────────────────────────────────────────────────────

export interface FilterSegment {
  /** Numeric type ID from the stored `filterText` column. */
  typeId: number;
  /** Human header (e.g. "Coordinators"). Comes from `FILTER_TYPE_HEADERS`. */
  header: string;
  /** Decoded labels, with the trailing `^` "overall" marker stripped. */
  labels: string[];
  /**
   * True iff any of the raw labels carried a `^` suffix — the "Apply to All"
   * marker assembled in admin_report_list.php lines 908-914. The viewer
   * appends `*` to the header when this is set (see
   * report_visual_display3.php lines 365-369).
   */
  overall: boolean;
}

/**
 * Authoritative filter type ID → header label registry.
 *
 * IDs 1-31 come from `$reportFilterArray` in legacy
 * [report_generator_autorun_pbi.php lines 372-403](html/html/report_generator_autorun_pbi.php).
 * The user-facing header labels used by the legacy table column come from
 * [dt-dashboard-reports-list2.php lines 330-349](html/html/lib/tables/dt-dashboard-reports-list2.php).
 * IDs 16-31 are not rendered in the legacy bottom table but exist in
 * stored data, so we include human labels for them too.
 */
export const FILTER_TYPE_HEADERS: Record<number, string> = {
  1:  'Coordinators',
  2:  'Nurses',
  3:  'Doctors',
  4:  'Receptionists',
  5:  'Facilities',
  6:  'Procedure Categories',
  7:  'Procedure Sub-Categories',
  8:  'Referral Categories',
  9:  'Referral Sub-Categories',
  10: 'Stages',
  11: 'Appointment Type Groups',
  12: 'Appointment Types',
  13: 'Merchant Accounts',
  14: 'Languages',
  15: 'Tags',
  16: 'Zip Code (in)',
  17: 'Zip Code (out)',
  18: 'Gender',
  19: 'Lead Sources',
  20: 'Plan Types',
  21: 'Interest Level',
  22: 'Plan Statuses',
  23: 'Procedure Count',
  24: 'Fees',
  25: 'Discounts',
  26: 'Memberships',
  27: 'Products',
  28: 'Packages',
  29: 'Cancel Reasons',
  30: 'Creators',
  31: 'Appointment Resources',
};

/**
 * Parses the legacy `filterText` column into per-type segments.
 *
 * Storage format (built in admin/admin_report_order3.php lines 605-697,
 * one segment per active filter type):
 *
 *     `{typeID}!{label1}^?|{label2}^?|...|;{typeID}!{labels}...;`
 *
 * - Segments separated by `;`
 * - Inside a segment: `typeID` then `!` then pipe-delimited labels
 * - A label may end with `^` indicating the corresponding type had the
 *   "Apply to All" / overall flag set
 * - The whole payload is URL-encoded (legacy decodes with `urldecode()`)
 *
 * Legacy mb_substr trims the trailing character before splitting on `!`
 * (lines 197). We do the same so a stray trailing `;` doesn't yield an
 * empty final segment.
 */
export function parseFilterText(raw: string | null | undefined): FilterSegment[] {
  if (!raw) return [];
  const out: FilterSegment[] = [];
  for (const segment of String(raw).split(';')) {
    const trimmed = segment.trim();
    if (!trimmed) continue;
    const decoded = safeUrlDecode(trimmed);
    // Legacy strips the last character (mb_substr ..., 0, -1) before splitting
    // on `!`. That's because the trailing `;` was previously stripped in the
    // explode but the original payload also embeds a redundant trailing pipe.
    // We replicate by stripping a single trailing `|` if present.
    const cleaned = decoded.endsWith('|') ? decoded.slice(0, -1) : decoded;
    const bangIdx = cleaned.indexOf('!');
    if (bangIdx < 0) continue;
    const typeId = Number(cleaned.slice(0, bangIdx));
    if (!Number.isFinite(typeId) || typeId <= 0) continue;
    const labelString = cleaned.slice(bangIdx + 1);
    if (!labelString) continue;
    const labels: string[] = [];
    let overall = false;
    for (const rawLabel of labelString.split('|')) {
      const decodedLabel = safeUrlDecode(rawLabel).trim();
      if (!decodedLabel) continue;
      if (decodedLabel.endsWith('^')) {
        overall = true;
        const stripped = decodedLabel.slice(0, -1).trim();
        if (stripped) labels.push(stripped);
      } else {
        labels.push(decodedLabel);
      }
    }
    if (labels.length === 0) continue;
    out.push({
      typeId,
      header: FILTER_TYPE_HEADERS[typeId] ?? `Filter ${typeId}`,
      labels,
      overall,
    });
  }
  return out;
}

function safeUrlDecode(s: string): string {
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    return s;
  }
}

/**
 * One-liner summary of parsed filters for the list column.
 * Format: `Header*: label1, label2 · Header: label1`
 * Matches the legacy concat shape from
 * dt-dashboard-reports-list2.php lines 330-349 where each `displayFilterList`
 * call returns a `<div>` block with `<strong>Header: </strong>labels`.
 */
export function formatFilterTextOneLine(raw: string | null | undefined): string {
  const segments = parseFilterText(raw);
  if (segments.length === 0) return '';
  return segments
    .map((s) => `${s.header}${s.overall ? '*' : ''}: ${s.labels.join(', ')}`)
    .join(' · ');
}
